import ReactGA from 'react-ga4';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';
////////////////////////////////////////////////////////////////////
import { PageTitle, MenuOption, BackButton } from '@/app/shared/components';
import { useCustomSelector } from '@/core/redux/hooks';
import { useEffect } from 'react';

/**
 * Settings component renders the settings page of the application.
 * It includes options for selecting a network and changing the language.
 *
 * Hooks:
 * - useTranslation: For accessing translation functions.
 * - useCustomSelector: To access the selected network from the Redux store.
 *
 * Effects:
 * - useEffect: Sends a pageview event to Google Analytics when the component mounts.
 *
 * Template:
 * - Renders a BackButton, PageTitle, and two MenuOptions for network and language settings.
 * - The language setting adapts the selected language based on the i18n configuration.
 */
const Settings: React.FC = () => {
	/*********************
	// #region Hooks
	**********************/
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { networkSelected } = useCustomSelector((state) => state.network);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/settings',
			title: 'Settings',
		});
	}, []);

	/*********************
	// #region Template
	**********************/
	return (
		<div className="flex flex-col gap-2 pt-8 pb-32 mx-4">
			<BackButton />
			<div className="flex flex-col items-center">
				<PageTitle>Settings</PageTitle>
			</div>
			<div className="flex flex-col w-full gap-4 mt-5">
				<MenuOption
					title={t('settings.options.chooseNetwork')}
					path="/settings/network"
					selected={networkSelected.name}
				/>

				<MenuOption
					title={t('settings.options.chooseLang')}
					path="/settings/language"
					selected={
						i18n.language === 'en'
							? t('languages.en')
							: i18n.language === 'es'
							? t('languages.es')
							: i18n.language === 'fr'
							? t('languages.fr')
							: i18n.language === 'de'
							? t('languages.de')
							: i18n.language === 'it'
							? t('languages.it')
							: i18n.language === 'pt'
							? t('languages.pt')
							: t('languages.en') // Default to English if no match
					}
				/>
			</div>
		</div>
	);
};

export default Settings;
